import type { ActivitySample, ChatActivity } from '../api/activity'

type ActivityData = Pick<ChatActivity, 'from' | 'to' | 'samples' | 'sampleIntervalMs'>

export type ActivityInspection = {
  /** The original sample slot's timestamp, or the inspected slot when missing. */
  timestampMs: number
  messagesPerSecond: number | null
}

export type ActivityInspectionSource = 'hover' | 'pinned' | 'keyboard'
export type ActivityInspectionState = { timestampMs: number; source: ActivityInspectionSource }

/** Resolve an inspected time to the original one-minute sample covering it. */
export function activitySampleAt(data: ActivityData, timestampMs: number): ActivityInspection {
  const clamped = Math.max(data.from, Math.min(data.to - 1, timestampMs))
  const slot = Math.floor(clamped / data.sampleIntervalMs) * data.sampleIntervalMs
  const sample = data.samples.find((candidate) => candidate.timestampMs === slot)
  return { timestampMs: slot, messagesPerSecond: sample?.messagesPerSecond ?? null }
}

/** Index of the stored sample closest to a time, or -1 when there are none. */
export function nearestSampleIndex(samples: ActivitySample[], timestampMs: number): number {
  if (samples.length === 0) return -1
  let low = 0
  let high = samples.length - 1
  while (low < high) {
    const middle = Math.floor((low + high) / 2)
    if (samples[middle].timestampMs < timestampMs) low = middle + 1
    else high = middle
  }
  const after = samples[low]
  const before = samples[low - 1]
  if (!before) return low
  return timestampMs - before.timestampMs <= after.timestampMs - timestampMs ? low - 1 : low
}

/** Next stored sample in the requested direction, including explicit null samples. */
export function stepSampleIndex(samples: ActivitySample[], timestampMs: number, direction: -1 | 1): number {
  if (samples.length === 0) return -1
  const exact = samples.findIndex((sample) => sample.timestampMs === timestampMs)
  if (exact >= 0) return Math.max(0, Math.min(samples.length - 1, exact + direction))
  if (direction > 0) {
    const after = samples.findIndex((sample) => sample.timestampMs > timestampMs)
    return after === -1 ? samples.length - 1 : after
  }
  for (let index = samples.length - 1; index >= 0; index--) {
    if (samples[index].timestampMs < timestampMs) return index
  }
  return 0
}
